/**
 * Feature Flags Configuration
 *
 * Define all feature flags here with their metadata.
 * Each flag can be rolled out progressively from 0% to 100%.
 */

export interface FeatureFlagConfig {
  name: string;
  description: string;
  enabled: boolean;
  rolloutPercentage: number; // 0-100
  owner: string;
  createdAt: Date;
  betaTesters?: string[]; // Email addresses of beta testers
  targetPhase?: 'ALPHA' | 'BETA' | 'RC' | 'GA';
  deprecationDate?: Date;
}

export const FEATURE_FLAGS: Record<string, FeatureFlagConfig> = {
  // ============================================================
  // FASE 0: Infrastructure & Data Migration
  // ============================================================
  'funil.data_migration': {
    name: 'funil.data_migration',
    description: 'Ativa novas tabelas e campos para funil (migrations)',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Backend Team',
    createdAt: new Date('2024-09-01'),
    targetPhase: 'ALPHA'
  },

  // ============================================================
  // FASE 1: Timeline Unificada
  // ============================================================
  'funil.timeline.view': {
    name: 'funil.timeline.view',
    description: 'Exibe /vendas/timeline/:clienteId com histórico completo',
    enabled: true,
    rolloutPercentage: 100,
    owner: 'Frontend Team',
    createdAt: new Date('2024-09-08'),
    targetPhase: 'BETA',
    betaTesters: []
  },

  // ============================================================
  // FASE 2: Pipeline Kanban
  // ============================================================
  'funil.pipeline.view': {
    name: 'funil.pipeline.view',
    description: 'Exibe /vendas/pipeline com kanban do funil',
    enabled: true,
    rolloutPercentage: 100,
    owner: 'Frontend Team',
    createdAt: new Date('2024-09-15'),
    targetPhase: 'BETA'
  },

  'funil.pipeline.drag_drop': {
    name: 'funil.pipeline.drag_drop',
    description: 'Permite drag & drop de cards no pipeline',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-09-15'),
    targetPhase: 'BETA'
  },

  // ============================================================
  // FASE 3: Contextualização
  // ============================================================
  'funil.context.preenchimento': {
    name: 'funil.context.preenchimento',
    description: 'Pré-preenchimento de cliente ao criar novo contrato',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-09-22'),
    targetPhase: 'BETA'
  },

  'funil.context.sugestoes': {
    name: 'funil.context.sugestoes',
    description: 'Sugestões inteligentes de próximas ações no funil',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-09-22'),
    targetPhase: 'BETA'
  },

  // ============================================================
  // FASE 4: Conversões & Automações
  // ============================================================
  'funil.lead_conversion': {
    name: 'funil.lead_conversion',
    description: 'Permitir conversão Lead → Cliente',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Backend Team',
    createdAt: new Date('2024-09-29'),
    targetPhase: 'BETA'
  },

  'funil.conversions.auto_notifications': {
    name: 'funil.conversions.auto_notifications',
    description: 'Notificações automáticas ao converter ou transitar no funil',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Backend Team',
    createdAt: new Date('2024-09-29'),
    targetPhase: 'BETA'
  },

  // ============================================================
  // FASE 5: Analytics Unificado
  // ============================================================
  'funil.analytics.dashboard': {
    name: 'funil.analytics.dashboard',
    description: 'Exibe /vendas/analytics com métricas do funil',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-10-06'),
    targetPhase: 'BETA'
  },

  'funil.analytics.reports': {
    name: 'funil.analytics.reports',
    description: 'Relatórios exportáveis do funil',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-10-06'),
    targetPhase: 'BETA'
  },

  // ============================================================
  // LEGACY: Navegação Antiga
  // ============================================================
  'legacy.navigation.visible': {
    name: 'legacy.navigation.visible',
    description: 'Mostra navegação antiga (/agendamentos, /clientes, /contratos)',
    enabled: true,
    rolloutPercentage: 100,
    owner: 'Product Team',
    createdAt: new Date('2024-01-01'),
    deprecationDate: new Date('2025-01-01'),
    targetPhase: 'GA'
  },

  'legacy.sidebar.old_layout': {
    name: 'legacy.sidebar.old_layout',
    description: 'Mostra sidebar antiga (antes do redesign)',
    enabled: false,
    rolloutPercentage: 0,
    owner: 'Frontend Team',
    createdAt: new Date('2024-01-01'),
    deprecationDate: new Date('2025-01-01')
  }
};

/**
 * Tipo para garantir type-safety ao usar flags
 */
export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

/**
 * Validar se uma flag existe
 */
export function isValidFeatureFlag(flagName: string): flagName is FeatureFlagName {
  return flagName in FEATURE_FLAGS;
}

/**
 * Obter configuração de uma flag
 */
export function getFeatureFlagConfig(flagName: FeatureFlagName): FeatureFlagConfig {
  return FEATURE_FLAGS[flagName];
}

/**
 * Listar todas as flags
 */
export function getAllFeatureFlags(): FeatureFlagConfig[] {
  return Object.values(FEATURE_FLAGS);
}

/**
 * Listar flags ativas
 */
export function getActiveFeatureFlags(): FeatureFlagConfig[] {
  return getAllFeatureFlags().filter(flag => flag.enabled);
}

/**
 * Listar flags em desenvolvimento (ALPHA/BETA)
 */
export function getBetaFeatureFlags(): FeatureFlagConfig[] {
  return getAllFeatureFlags().filter(
    flag => flag.targetPhase === 'ALPHA' || flag.targetPhase === 'BETA'
  );
}
